import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactDOM from "react-dom";
import { Tag, Row, Col, Tooltip as AntTooltip } from "antd";
import {
  LineChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Line,
  ResponsiveContainer,
} from "recharts";
import scalesConfig from "../../../../helpers/physioscaleskeys";
import NoDataPage from "../../../NoDataPages/NoDataPage";
import scaleIcon from "../../../../assets/images/rating-scale 1.svg";
import { ReloadOutlined } from "@ant-design/icons";

const clamp = (v, min, max) => Math.min(Math.max(v, min), max);

const getPointerClient = (e) => {
  const src = e?.nativeEvent || e;
  const t = src?.touches?.[0] || src?.changedTouches?.[0];
  const x = t?.clientX ?? src?.clientX;
  const y = t?.clientY ?? src?.clientY;
  if (typeof x !== "number" || typeof y !== "number") return null;
  if (Number.isNaN(x) || Number.isNaN(y)) return null;
  return { x, y };
};

const ProgressChart = ({ scaleKey, tags, chartData, subItemKey, height }) => {
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [tooltipSuppressed, setTooltipSuppressed] = useState(false);
  const [isCoarsePointer, setIsCoarsePointer] = useState(false);
  const chartWrapRef = useRef(null);
  const lastPointerRef = useRef(null);

  useEffect(() => {
    const mq = window.matchMedia("(pointer: coarse)");
    const onChange = () => setIsCoarsePointer(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  const romYAxisDomain =
    scaleKey === "ROM"
      ? ["auto", "auto"]
      : scaleKey === "ASIA"
        ? [0, 30]
        : [0, 10];

  const activeKeys = useMemo(
    () => (selectedKeys.length === 0 ? tags : selectedKeys),
    [selectedKeys, tags]
  );


  useEffect(() => {
    const hideOnScroll = (e) => {
      if (e?.target?.closest?.(".ip-progress-fixed-tooltip")) return;
      setTooltipSuppressed(true);
    };
    const scroller =
      chartWrapRef.current?.closest(
        ".ip-scale-drawer-content, .op-scale-drawer-content, .ant-drawer-body"
      ) || null;

    scroller?.addEventListener("scroll", hideOnScroll, { passive: true });
    window.addEventListener("scroll", hideOnScroll, {
      passive: true,
      capture: true,
    });
    return () => {
      scroller?.removeEventListener("scroll", hideOnScroll);
      window.removeEventListener("scroll", hideOnScroll, { capture: true });
    };
  }, []);

  const tooltipFormatter = (value, name, props) => {
    if (scaleKey !== "ASIA") return value;
    const row = props?.payload || {};

    if (name === "asia_impairment_scale") {
      return [row._asia_grade_label ?? value, "ASIA Impairment Scale"];
    }
    if (name === "nerological_level_of_injury") {
      return [row._nli_label ?? value, "Neurological Level of Injury"];
    }
    return value;
  };

  const FixedTooltip = ({ active, payload, label, coordinate }) => {
    const tooltipRef = useRef(null);
    const [pos, setPos] = useState(null);

    const isRom = scaleKey === "ROM";
    const maxW = isRom ? 560 : 320;
    const maxH = Math.min(
      isRom ? 560 : 420,
      Math.max(200, window.innerHeight - 120)
    );

    const visible = Boolean(
      !tooltipSuppressed &&
        active &&
        payload?.length &&
        coordinate &&
        chartWrapRef.current
    );

   
    useLayoutEffect(() => {
      if (!visible) {
        setPos(null);
        return;
      }

      const rect = chartWrapRef.current.getBoundingClientRect();
      const pointer = lastPointerRef.current;
      const anchorX =
        typeof coordinate?.x === "number"
          ? rect.left + coordinate.x
          : (pointer?.x ?? rect.left);
      const anchorY =
        typeof coordinate?.y === "number"
          ? rect.top + coordinate.y
          : (pointer?.y ?? rect.top);

      const el = tooltipRef.current;
      const w = Math.min(el?.offsetWidth || maxW, maxW);
      const h = Math.min(el?.offsetHeight || maxH, maxH);

      const pad = 12;
      let left = anchorX + 16;
      let top = anchorY - 12;

      if (left + w > window.innerWidth - pad) left = anchorX - w - 16;
      if (top + h > window.innerHeight - pad) top = anchorY - h - 12;

      left = clamp(left, pad, Math.max(pad, window.innerWidth - w - pad));
      top = clamp(top, pad, Math.max(pad, window.innerHeight - h - pad));

      setPos({ left, top });
    }, [visible, coordinate?.x, coordinate?.y, label, payload?.length, maxW, maxH]);

    if (!visible) return null;

    const header = label || payload?.[0]?.payload?.name || "";

    const rows = payload
      .map((p, idx) => {
        const formatted = tooltipFormatter(p?.value, p?.dataKey, {
          payload: p?.payload,
        });
        const tuple = Array.isArray(formatted)
          ? formatted
          : [formatted, p?.name];
        const val = tuple[0];
        const lbl = tuple[1] ?? p?.name ?? p?.dataKey ?? "-";
        if (val === null || val === undefined || val === "") return null;
        return {
          key: `${p?.dataKey ?? p?.name ?? "k"}-${idx}`,
          label: lbl,
          value: val,
        };
      })
      .filter(Boolean);

    const romCols = isRom
      ? rows.length > 24
        ? 3
        : rows.length > 12
          ? 2
          : 1
      : 1;

    const node = (
      <div
        ref={tooltipRef}
        className="ip-progress-fixed-tooltip"
        style={{
          position: "fixed",
          left: pos?.left ?? 0,
          top: pos?.top ?? 0,
          visibility: pos ? "visible" : "hidden",
          zIndex: 10000,
          pointerEvents: isCoarsePointer ? "auto" : "none",
          backgroundColor: "rgba(31, 31, 31, 0.92)",
          borderRadius: 10,
          color: "#ffffff",
          maxWidth: maxW,
          maxHeight: maxH,
          padding: 12,
          whiteSpace: "normal",
          border: "1px solid rgba(255,255,255,0.10)",
          boxShadow: "0 8px 18px rgba(0,0,0,0.35)",
        }}
      >
        {header ? (
          <div style={{ fontWeight: 700, marginBottom: 8, fontSize: 13 }}>
            {header}
          </div>
        ) : null}
        <div
          style={{
            display: isRom ? "grid" : "flex",
            gridTemplateColumns: isRom
              ? `repeat(${romCols}, minmax(0, 1fr))`
              : undefined,
            columnGap: isRom ? 16 : undefined,
            rowGap: 6,
            flexDirection: isRom ? undefined : "column",
            gap: isRom ? undefined : 6,
            overflow: "auto",
            maxHeight: maxH - 40,
            overscrollBehavior: "contain",
            WebkitOverflowScrolling: "touch",
            touchAction: "pan-y",
          }}
        >
          {rows.map((r) => (
            <div
              key={r.key}
              style={{
                fontSize: isRom ? 11 : 12,
                lineHeight: isRom ? "14px" : "16px",
                minWidth: 0,
              }}
            >
              <span style={{ fontWeight: 600 }}>{r.label}</span>
              <span>{`: ${r.value}`}</span>
            </div>
          ))}
        </div>
      </div>
    );

    return ReactDOM.createPortal(node, document.body);
  };

  const handleTagClick = (key) => {
    setSelectedKeys((prevSelected) =>
      prevSelected.includes(key)
        ? prevSelected.filter((item) => item !== key)
        : [...prevSelected, key]
    );
  };

  if (!chartData || chartData.length === 0) {
    return (
      <NoDataPage
        title="No data was recorded for this scale"
        subtitle=""
        image={scaleIcon}
      />
    );
  }

  return (
    <>
      <Row gutter={[8, 8]} justify="center" style={{ marginBottom: 16 }}>
        {tags.map((tagKey, index) => {
          const tagConfig = subItemKey
            ? scalesConfig[scaleKey]?.subItems?.[subItemKey]?.keys?.[tagKey]
            : scalesConfig[scaleKey]?.keys?.[tagKey] || {};

          const isSelected = selectedKeys.includes(tagKey);

          return (
            <Col key={index}>
              <Tag
                style={{
                  fontSize: "12px",
                  fontWeight: "200",
                  backgroundColor: isSelected ? tagConfig?.color : "white",
                  borderColor: tagConfig?.color,
                  color: isSelected ? "white" : tagConfig?.color,
                  cursor: "pointer",
                }}
                onClick={() => handleTagClick(tagKey)}
              >
                {tagConfig?.name || tagKey.replace(/_/g, " ")}
              </Tag>
            </Col>
          );
        })}
        <Col span={2}>
          <AntTooltip title="Clear All">
            <Tag
              style={{ cursor: "pointer" }}
              onClick={() => setSelectedKeys([])}
            >
              <ReloadOutlined />
            </Tag>
          </AntTooltip>
        </Col>
        <Col span={24}>
          <div
            ref={chartWrapRef}
            className="ip-progress-chart-wrap"
            style={{ width: "100%", height: height }}
            onTouchStart={(e) => {
              lastPointerRef.current = getPointerClient(e);
              setTooltipSuppressed(false);
            }}
            onMouseDown={(e) => {
              lastPointerRef.current = getPointerClient(e);
              setTooltipSuppressed(false);
            }}
            onMouseMove={(e) => {
              lastPointerRef.current = getPointerClient(e);
            }}
          >
            <ResponsiveContainer>
              <LineChart
                data={chartData}
                margin={{ top: 16, right: 48, bottom: 48, left: 8 }}
                onMouseMove={(state, e) => {
                  const pointer = getPointerClient(e);
                  if (pointer) lastPointerRef.current = pointer;
                  if (tooltipSuppressed) setTooltipSuppressed(false);
                }}
                onClick={(state, e) => {
                  const pointer = getPointerClient(e);
                  if (pointer) lastPointerRef.current = pointer;
                  if (tooltipSuppressed) setTooltipSuppressed(false);
                }}
              >
                <CartesianGrid
                  stroke="#eee"
                  strokeDasharray="5 5"
                  vertical={false}
                />
                <XAxis
                  dataKey="name"
                  interval="preserveStartEnd"
                  minTickGap={24}
                  tickMargin={10}
                  tick={{ fontSize: 11, fill: "#667085" }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis domain={romYAxisDomain} tick={{ fontSize: 12 }} />
                <Tooltip
                  content={<FixedTooltip />}
                  wrapperStyle={{
                    display: "none",
                    visibility: "hidden",
                    pointerEvents: "none",
                  }}
                  cursor={{ stroke: "#98A2B3", strokeDasharray: "4 4" }}
                />
                {activeKeys.map((key) => {
                  const keyConfig = subItemKey
                    ? scalesConfig[scaleKey]?.subItems?.[subItemKey]?.keys?.[key]
                    : scalesConfig[scaleKey]?.keys?.[key] || {};

                  return (
                    <Line
                      key={key}
                      type="monotone"
                      dataKey={key}
                      stroke={keyConfig?.color || "#8884d8"}
                      strokeWidth={1.5}
                      connectNulls={true}
                      dot={{ r: 4, strokeWidth: 1 }}
                      activeDot={{ r: 6 }}
                      name={key
                        .replace(/_/g, " ")
                        .replace(/^(\w)/, (c) => c.toUpperCase())}
                    />
                  );
                })}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Col>
      </Row>
    </>
  );
};

export default ProgressChart;
